import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Tooltip } from "antd";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiChevronRight, mdiInformationOutline, mdiMinus, mdiPlus } from "@mdi/js";
import useBreakpoint from "../../../../utils/useBreakpoint";
import ScrollCarousel from "../../v2/components/ScrollCarousel";
import { LandTourDetail, LandTourCategory, LandTourPriceDate } from "../../../../interfaces/land-tour.interface";
import GroundTourModal from "./GroundTourModal";
import SurchargesModal from "./SurchargesModal";

const ICONS = "/iceholidays-assets/images/icons";

// Past this length the chip clamps to 3 lines and gets an info icon + tooltip for the full text.
const LONG_CATEGORY_LABEL_THRESHOLD = 50;

export interface PaxCounts {
  adult: number;
  single_supplement: number;
  child_with_bed: number;
  child_without_bed: number;
  child_twin: number;
  infant: number;
}

export const EMPTY_PAX_COUNTS: PaxCounts = {
  adult: 0,
  single_supplement: 0,
  child_with_bed: 0,
  child_without_bed: 0,
  child_twin: 0,
  infant: 0,
};

export const PAX_TYPES: { key: keyof PaxCounts; label: string }[] = [
  { key: "adult", label: "Adult" },
  { key: "single_supplement", label: "Adult (Single Room)" },
  { key: "child_with_bed", label: "Child with Extra bed" },
  { key: "child_without_bed", label: "Child without bed" },
  { key: "child_twin", label: "Child twin" },
  { key: "infant", label: "Infant" },
];

// A single departure date plus the LandTourPriceDate window it belongs to. One
// LandTourPriceDate backs many individual dates (a departure window), so the
// window alone can't identify which date the user picked - see the comment on
// firstDateSelection below for why this used to be a bug.
export interface DateSelection {
  date: string;
  priceDate: LandTourPriceDate;
}

interface Props {
  landTour: LandTourDetail;
  selectedCategory: LandTourCategory | undefined;
  onCategoryChange: (category: LandTourCategory) => void;
  selectedDate: DateSelection | undefined;
  onDateChange: (selection: DateSelection) => void;
  paxCounts: PaxCounts;
  onPaxCountsChange: (counts: PaxCounts) => void;
}

// The earliest bookable date for a category, used both as the initial selection
// on load and whenever the category switches. Scans price_dates directly rather
// than depending on this component's internal dateOptions memo, so the parent
// page can compute it too without lifting that memo.
export const firstDateSelection = (category: LandTourCategory | undefined): DateSelection | undefined => {
  if (!category) return undefined;
  let earliest: DateSelection | undefined;
  category.price_dates.forEach((priceDate) => {
    priceDate.date.forEach((date) => {
      if (!earliest || date < earliest.date) earliest = { date, priceDate };
    });
  });
  return earliest;
};

// The active pricing tier for a given price-date is picked by total headcount
// (adults + children; infants typically don't occupy a seat/bed), same idea as
// the series booking form's per-tier price lookup, but LandTour's quantities
// are already expanded arrays so no range-string parsing is needed.
export const totalPax = (counts: PaxCounts) =>
  counts.adult + counts.single_supplement + counts.child_with_bed + counts.child_without_bed + counts.child_twin;

// Tiers sorted by their lowest quantity, ascending - the jbuilder emits them in
// unsorted hash order (app/views/api/v1/land_tours/show.json.jbuilder), and
// LandTours::PriceQuote (iceholidays#4453) explicitly sorts by range start
// before picking a "first tier", so usePriceQuote's local fallback needs the
// same ordering to agree with the server. Exported so that fallback can reuse it.
export const sortedTiers = (priceDate: LandTourPriceDate | undefined) =>
  [...(priceDate?.quantities || [])].sort((a, b) => (a.quantity[0] ?? 0) - (b.quantity[0] ?? 0));

export const pricingTierFor = (priceDate: LandTourPriceDate | undefined, counts: PaxCounts) => {
  if (!priceDate) return undefined;
  const tiers = sortedTiers(priceDate);
  if (!tiers.length) return undefined;
  const pax = Math.max(totalPax(counts), 1);
  // Tiered pricing decreases monotonically as headcount grows, so falling back to
  // the nearest tier only makes sense on the low side (pax below every tier's
  // range, e.g. the traveller count dipped under min_booking mid-edit) - clamp to
  // the cheapest/first tier there. Falling through to the *last* tier here would
  // silently quote the group rate for an under-count instead of the entry rate.
  return (
    tiers.find((tier) => tier.quantity.includes(pax)) ||
    (pax < (tiers[0].quantity[0] ?? 0) ? tiers[0] : tiers[tiers.length - 1])
  );
};

export const priceForPaxType = (
  priceDate: LandTourPriceDate | undefined,
  counts: PaxCounts,
  type: keyof PaxCounts
): number => {
  if (type === "single_supplement") return priceDate?.singleSupplementPrice || 0;
  const tier = pricingTierFor(priceDate, counts);
  if (!tier) return 0;
  if (type === "adult") return tier.adult;
  return tier.child_prices.find((cp) => cp.type === type)?.price || 0;
};

export default function ChooseYourTour({
  landTour,
  selectedCategory,
  onCategoryChange,
  selectedDate,
  onDateChange,
  paxCounts,
  onPaxCountsChange,
}: Props) {
  const isMobile = useBreakpoint();
  const [showAllDates, setShowAllDates] = useState(false);
  const [showSurcharges, setShowSurcharges] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState<string | null>(null);

  // Availability can span many months (this tour has ~16 months of open dates),
  // so the strip shows one month at a time - matching the Figma strip's size -
  // instead of rendering every individual day as a chip in one giant row.
  const dateOptions = useMemo(() => {
    if (!selectedCategory) return [];
    const seen = new Set<string>();
    const options: { date: string; priceDate: LandTourPriceDate }[] = [];

    selectedCategory.price_dates.forEach((priceDate) => {
      priceDate.date.forEach((date) => {
        if (seen.has(date)) return;
        seen.add(date);
        options.push({ date, priceDate });
      });
    });

    return options.sort((a, b) => a.date.localeCompare(b.date));
  }, [selectedCategory]);

  const months = useMemo(() => {
    const seen = new Set<string>();
    dateOptions.forEach(({ date }) => seen.add(date.slice(0, 7)));
    return Array.from(seen).sort();
  }, [dateOptions]);

  useEffect(() => {
    setVisibleMonth(months[0] || null);
  }, [months]);

  const visibleDateOptions = useMemo(
    () => dateOptions.filter(({ date }) => date.slice(0, 7) === visibleMonth),
    [dateOptions, visibleMonth]
  );

  const monthIndex = visibleMonth ? months.indexOf(visibleMonth) : -1;

  // A surcharge with no categories listed applies tour-wide; otherwise it only applies to the
  // categories it names. Previously every surcharge was shown regardless of the selected
  // category (issue #629 (b)6 asks for this scoped to the category).
  const categorySurcharges = useMemo(
    () =>
      landTour.surcharges.filter(
        (surcharge) =>
          !surcharge.categories?.length || surcharge.categories.some((category) => category.id === selectedCategory?.id)
      ),
    [landTour.surcharges, selectedCategory]
  );

  const setPaxCount = (key: keyof PaxCounts, delta: number) => {
    // Adult never goes below the tour's minimum booking size (maxBooking/maxBookingArr were
    // fetched but previously never read at all, so every other pax type could be decremented
    // freely and adult could reach 0 despite a stated minimum - issue #629 bug B8).
    const floor = key === "adult" ? Math.max(landTour.minBooking || 1, 1) : 0;
    const next = Math.max(floor, paxCounts[key] + delta);
    const nextCounts = { ...paxCounts, [key]: next };

    const maxBooking = selectedDate?.priceDate.maxBooking;
    if (delta > 0 && maxBooking && totalPax(nextCounts) > maxBooking) return;

    onPaxCountsChange(nextCounts);
  };

  if (!landTour.pricingCategories.length) return null;

  return (
    <section className="ground-tour-show-v2__choose">
      <h2 className="ground-tour-show-v2__section-title">Tour Category</h2>

      <div className="ground-tour-show-v2__category-list">
        {landTour.pricingCategories.map((category) => {
          const isLong = category.category.length > LONG_CATEGORY_LABEL_THRESHOLD;

          return (
            <button
              key={category.id}
              type="button"
              className={`ground-tour-show-v2__category-chip ${selectedCategory?.id === category.id ? "is-selected" : ""}`}
              onClick={() => onCategoryChange(category)}
            >
              <span className="ground-tour-show-v2__category-chip-label">{category.category}</span>

              {isLong && (
                <Tooltip title={category.category}>
                  <span className="ground-tour-show-v2__category-chip-info">
                    <Icon path={mdiInformationOutline} size="16px" />
                  </span>
                </Tooltip>
              )}
            </button>
          );
        })}
      </div>

      <div className="ground-tour-show-v2__date-picker">
        <div className="ground-tour-show-v2__date-picker-header">
          <h3>Select Date</h3>

          {months.length > 1 && visibleMonth && (
            <div className="ground-tour-show-v2__month-nav">
              <button
                type="button"
                disabled={monthIndex <= 0}
                onClick={() => setVisibleMonth(months[monthIndex - 1])}
                aria-label="Previous month"
              >
                <Icon path={mdiChevronLeft} size="18px" />
              </button>
              <span>{dayjs(`${visibleMonth}-01`).format("MMMM YYYY")}</span>
              <button
                type="button"
                disabled={monthIndex >= months.length - 1}
                onClick={() => setVisibleMonth(months[monthIndex + 1])}
                aria-label="Next month"
              >
                <Icon path={mdiChevronRight} size="18px" />
              </button>
            </div>
          )}
        </div>

        <div className="ground-tour-show-v2__date-strip">
          <button type="button" className="ground-tour-show-v2__view-all-dates" onClick={() => setShowAllDates(true)}>
            <img src={`${ICONS}/icon-calendar-red.svg`} alt="" aria-hidden="true" />
            View All Dates
          </button>

          <ScrollCarousel trackClassName="ground-tour-show-v2__date-track" step={100}>
            {visibleDateOptions.map(({ date, priceDate }) => {
              const parsed = dayjs(date);
              const isSelected = selectedDate?.date === date;

              return (
                <button
                  key={date}
                  type="button"
                  className={`ground-tour-show-v2__date-chip ${isSelected ? "is-selected" : ""}`}
                  onClick={() => onDateChange({ date, priceDate })}
                >
                  <span className="ground-tour-show-v2__date-year">{parsed.format("YYYY")}</span>
                  <span className="ground-tour-show-v2__date-day">{parsed.format("DD MMM")}</span>
                </button>
              );
            })}
          </ScrollCarousel>
        </div>
      </div>

      <div className="ground-tour-show-v2__travellers">
        <h3>Travellers {landTour.minBooking ? `(Min. ${landTour.minBooking} Pax)` : ""}</h3>

        <div className="ground-tour-show-v2__pax-grid">
          {PAX_TYPES.map(({ key, label }) => {
            const price = priceForPaxType(selectedDate?.priceDate, paxCounts, key);
            // Already live (BE #4358); previously unmapped, so child pax cards showed price
            // only with no age copy (issue #629 (b)6).
            const ageRange = landTour.childAgeRanges?.[key];

            // Mirrors setPaxCount's own bounds so the stepper buttons reflect what a press would do.
            const floor = key === "adult" ? Math.max(landTour.minBooking || 1, 1) : 0;
            const maxBooking = selectedDate?.priceDate.maxBooking;
            const atFloor = paxCounts[key] <= floor;
            const atCeiling = !!maxBooking && totalPax(paxCounts) + 1 > maxBooking;

            const meta = [
              ageRange ? `${ageRange.min_age}-${ageRange.max_age} yrs` : null,
              price > 0 ? `RM ${price.toFixed(2)}` : null,
            ].filter(Boolean).join(" • ");

            return (
              <div className="ground-tour-show-v2__pax-card" key={key}>
                <div className="ground-tour-show-v2__pax-info">
                  <span className="ground-tour-show-v2__pax-label">{label}</span>
                  {meta && <span className="ground-tour-show-v2__pax-meta">{meta}</span>}
                </div>

                <div className="ground-tour-show-v2__pax-stepper">
                  <button
                    type="button"
                    onClick={() => setPaxCount(key, -1)}
                    disabled={atFloor}
                    aria-label={`Decrease ${label}`}
                  >
                    <Icon path={mdiMinus} size="16px" />
                  </button>
                  <span>{paxCounts[key]}</span>
                  <button
                    type="button"
                    onClick={() => setPaxCount(key, 1)}
                    disabled={atCeiling}
                    aria-label={`Increase ${label}`}
                  >
                    <Icon path={mdiPlus} size="16px" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {categorySurcharges.length > 0 && (
          <div className="ground-tour-show-v2__surcharge-row">
            <span>Surcharges applied</span>
            <button type="button" onClick={() => setShowSurcharges(true)}>
              View details
              <img src={`${ICONS}/icon-open-arrow.png`} alt="" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>

      <GroundTourModal open={showAllDates} onClose={() => setShowAllDates(false)} title="All Available Dates" isMobile={isMobile} width={720}>
        <div className="ground-tour-show-v2__all-dates-months">
          {months.map((month) => (
            <div className="ground-tour-show-v2__all-dates-month" key={month}>
              <h4>{dayjs(`${month}-01`).format("MMMM YYYY")}</h4>
              <div className="ground-tour-show-v2__all-dates-grid">
                {dateOptions
                  .filter(({ date }) => date.slice(0, 7) === month)
                  .map(({ date, priceDate }) => (
                    <button
                      key={date}
                      type="button"
                      className={`ground-tour-show-v2__date-chip ${selectedDate?.date === date ? "is-selected" : ""}`}
                      onClick={() => {
                        onDateChange({ date, priceDate });
                        setVisibleMonth(month);
                        setShowAllDates(false);
                      }}
                    >
                      {dayjs(date).format("DD")}
                    </button>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </GroundTourModal>

      <SurchargesModal open={showSurcharges} onClose={() => setShowSurcharges(false)} surcharges={categorySurcharges} />
    </section>
  );
}
